'use client'

import { State } from '@/payload-types'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

type StateOption = Pick<State, 'id' | 'name' | 'slug'>

type StateSelectorDialogProps = {
  states: StateOption[]
  currentState?: StateOption
  /**
   * Template used to build each state's destination URL. Use `{slug}` as the
   * placeholder for the state slug. Defaults to `/states/{slug}`.
   */
  hrefPattern?: string
  /** Label shown when no current state is set. */
  placeholder?: string
  /** Optional override for the trigger button's className. */
  triggerClassName?: string
}

export function StateSelectorDialog({
  states,
  currentState,
  hrefPattern = '/states/{slug}',
  placeholder = 'Select State',
  triggerClassName,
}: StateSelectorDialogProps) {
  const buildHref = (slug: string) => hrefPattern.replaceAll('{slug}', slug)
  const [open, setOpen] = useState(false)

  const groupedStates = states.reduce((acc, state) => {
    const firstLetter = state.name.charAt(0).toUpperCase()
    if (!acc[firstLetter]) {
      acc[firstLetter] = []
    }
    acc[firstLetter].push(state)
    return acc
  }, {} as Record<string, StateOption[]>)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={
            triggerClassName ??
            'bg-black px-4 py-2 text-white rounded-full flex flex-row items-center text-sm gap-1'
          }
        >
          {currentState?.name || placeholder}
          <ChevronDown className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="end">
        <Command>
          <CommandInput placeholder="Search for your state..." />
          <CommandList>
            {Object.entries(groupedStates).sort().map(([letter, statesInGroup]) => (
              <CommandGroup key={letter} heading={letter}>
                {statesInGroup.map((state) => (
                  <a
                    href={state.slug ? buildHref(state.slug) : '#'}
                    key={state.id}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      className="cursor-pointer"
                      value={state.slug ?? ''}
                    >
                      {state.name}
                    </CommandItem>
                  </a>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}